import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

/**
 * NotificationDeepLinkHandler
 *
 * Handles deep link navigation from notifications on ALL platforms.
 * Centralizes every signaling path so we never miss a tap regardless
 * of which platform/wrapper combination is in play:
 *
 * 1. `window.onNotificationEvent(payload)` — official Despia hook for
 *    OneSignal taps (iOS Despia + modern Android Despia builds).
 * 2. **Server-side recovery** via `/api/community/notifications/recent-deep-link`.
 *    Fires on mount AND on every `visibilitychange` (so warm-resume
 *    notification taps are caught on Android Despia where Despia's
 *    `onNotificationEvent` doesn't fire reliably).
 * 3. `window.eiliHandleNotificationClick(url)` — JS bridge for legacy
 *    native wrappers.
 * 4. OneSignal Web SDK click event — for web push notifications.
 * 5. `popstate` listener — defensive fallback for older Android
 *    WebView builds that don't auto-rerender React Router on pushState.
 *
 * Back-button preservation:
 *  When a cold-start recovery navigates to a deep link, the browser's
 *  history would only contain `[deepLink]` — pressing back exits the
 *  app. To fix this, we replace the initial entry with `/home` THEN
 *  push the deep link, so back returns to `/home`.
 */

const RECOVERY_ENDPOINT = '/api/community/notifications/recent-deep-link?max_age_seconds=300';
const SESSION_KEY = 'eili_recovery_used_this_session';

export default function NotificationDeepLinkHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  const lastPathRef = useRef(location.pathname);
  const recoveryInProgressRef = useRef(false);

  useEffect(() => {
    lastPathRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    const navigateToUrl = (rawUrl, opts = {}) => {
      try {
        if (!rawUrl) return;
        let path = String(rawUrl).trim();
        if (!path) return;

        // Strip our own origin so React Router can take over.
        try {
          const u = new URL(path, window.location.origin);
          if (u.origin === window.location.origin) {
            path = u.pathname + u.search + u.hash;
          } else {
            window.location.href = path;
            return;
          }
        } catch {
          if (!path.startsWith('/')) path = '/' + path;
        }

        if (!path || path === lastPathRef.current) return;

        // Back-button preservation: when we deep-link from a bare/launch
        // path (cold-start case), replace "/" with "/home" first so the
        // user's back button returns to /home instead of exiting the app.
        // Skip this when the user was already on a meaningful page (warm
        // resume) — they have their own history to walk back through.
        const currentPath = window.location.pathname;
        const fromBarePath = ['/', '/app', ''].includes(currentPath);
        const targetIsAppHome = path === '/home' || path === '/app' || path.startsWith('/home?') || path.startsWith('/home#');

        if (opts.preserveBack && fromBarePath && !targetIsAppHome) {
          // Step 1: replace "/" with "/home" so it sits in the history
          //   stack but isn't the active entry.
          navigate('/home', { replace: true });
          // Step 2: push the deep link on top so back returns to /home.
          //   Use queueMicrotask so React Router commits step 1 first.
          queueMicrotask(() => {
            console.log('[NotificationDeepLink] navigate → ', path, '(with /home in stack)');
            navigate(path);
          });
        } else {
          console.log('[NotificationDeepLink] navigate → ', path);
          navigate(path);
        }
      } catch (err) {
        console.warn('[NotificationDeepLink] navigation failed:', err);
      }
    };

    // Server-side recovery — covers ALL the cases where the wrapper
    // fails to deliver `data.path` to JS:
    //   • Android Despia cold-start where INotificationClickListener
    //     isn't registered → app opens to /, recovery pulls the path.
    //   • Background→foreground resume where `onNotificationEvent`
    //     doesn't fire reliably.
    //   • Older Despia builds without the modern click bridge.
    // Atomic find_and_update on the backend ensures we never double-fire.
    const tryServerRecovery = async () => {
      try {
        if (recoveryInProgressRef.current) return;
        recoveryInProgressRef.current = true;
        try {
          const res = await axios.get(RECOVERY_ENDPOINT, {
            withCredentials: true,
            timeout: 4000,
          });
          const dl = res?.data?.deep_link;
          if (dl) {
            try { sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* ignore */ }
            navigateToUrl(dl, { preserveBack: true });
          }
        } catch {
          // 401 (not authed yet) / network error — ignore, will retry
          // on the next visibilitychange or focus.
        } finally {
          recoveryInProgressRef.current = false;
        }
      } catch { /* ignore */ }
    };

    // 1) Despia OneSignal click hook — fires for every notification tap
    //    on iOS Despia AND Android Despia. Despia first applies the
    //    `data.path` to the URL via pushState, then calls this handler
    //    so we can navigate explicitly (React Router doesn't always
    //    rerender on a raw pushState).
    window.onNotificationEvent = (payload) => {
      try {
        if (!payload || payload.type !== 'open') return;
        console.log('[NotificationDeepLink] Despia event:', payload);
        // Prefer `path` (relative, no reload). Fall back to `url` if
        // only the legacy field was sent.
        const target = payload.path || payload.url;
        if (target) {
          try { sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* ignore */ }
          navigateToUrl(target, { preserveBack: true });
        }
      } catch (err) {
        console.warn('[NotificationDeepLink] onNotificationEvent failed:', err);
      }
    };

    // 2) Global JS bridge for legacy native wrappers.
    window.eiliHandleNotificationClick = navigateToUrl;

    // 3) Drain pending clicks from before mount.
    if (Array.isArray(window.__eili_pending_notification_clicks__)) {
      const pending = window.__eili_pending_notification_clicks__;
      window.__eili_pending_notification_clicks__ = [];
      pending.forEach((url) => {
        try { navigateToUrl(url); } catch { /* ignore */ }
      });
    }
    // If Despia buffered a payload before this handler mounted
    // (cold-start case), replay it now.
    if (window.__eili_pending_notification_event__) {
      const buffered = window.__eili_pending_notification_event__;
      window.__eili_pending_notification_event__ = null;
      try {
        const target = buffered.path || buffered.url;
        if (target) {
          try { sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* ignore */ }
          navigateToUrl(target, { preserveBack: true });
        }
      } catch { /* ignore */ }
    }

    // 4) Visibility change — detect when the app resumes from a
    //    notification tap. Two cases handled:
    //    a) Despia's `data.path` flow updated the URL via pushState
    //       while we were backgrounded — sync React Router.
    //    b) Despia Android cold-start / warm-resume where NO URL update
    //       fires — call the server-side recovery endpoint to fetch the
    //       most recent unconsumed deep-link.
    const onVisibilityChange = () => {
      if (document.hidden) return;
      const currentPath = window.location.pathname + window.location.search + window.location.hash;
      if (currentPath && currentPath !== '/' && currentPath !== lastPathRef.current) {
        console.log('[NotificationDeepLink] URL changed while backgrounded:', currentPath);
        navigateToUrl(currentPath);
      }
      // Always poll server recovery on resume — the endpoint is cheap,
      // atomic, and the dominant cause of Android push-tap failures is
      // a missed click handler (no JS event fires at all).
      tryServerRecovery();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    // Cold-start recovery — wait one tick so axios cookies are ready,
    // then attempt the server recovery once. This is the primary fix
    // for Android Despia where the WebView opens to `/` with no JS
    // hook to tell us a notification was tapped.
    if (typeof sessionStorage !== 'undefined' && !sessionStorage.getItem(SESSION_KEY)) {
      setTimeout(() => tryServerRecovery(), 250);
    }

    // 5) `popstate` listener — Despia fires popstate after applying
    //    `data.path`. React Router v6 normally handles this, but on
    //    older Android WebView builds we force-sync to be safe.
    const onPopState = () => {
      const currentPath = window.location.pathname + window.location.search + window.location.hash;
      if (currentPath !== lastPathRef.current) {
        navigateToUrl(currentPath);
      }
    };
    window.addEventListener('popstate', onPopState);

    // 6) OneSignal Web SDK click listener — desktop browsers + PWAs.
    const onWebClick = (event) => {
      const url =
        event?.result?.url ||
        event?.notification?.launchURL ||
        event?.notification?.additionalData?.path ||
        event?.notification?.additionalData?.url ||
        event?.url;
      if (url) navigateToUrl(url);
    };
    try {
      window.OneSignalDeferred = window.OneSignalDeferred || [];
      window.OneSignalDeferred.push(function (OneSignal) {
        try {
          OneSignal.Notifications.addEventListener('click', onWebClick);
        } catch { /* ignore */ }
      });
    } catch { /* ignore */ }

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('popstate', onPopState);
      try { delete window.eiliHandleNotificationClick; } catch { /* ignore */ }
      try { delete window.onNotificationEvent; } catch { /* ignore */ }
    };
  }, [navigate]);

  return null;
}
